import { createEffect, createRoot, on } from 'solid-js'
import { clientToWorld } from '../canvas/viewportRef.ts'
import { activeTool, setLiveStroke, style, useTool } from '../editor.ts'
import { createPolyline } from '../scene/factory.ts'
import { addNodes, scene, select } from '../scene/store.ts'
import type { Point } from '../scene/types.ts'
import type { Tool } from './types.ts'

/** Screen pixels within which the pointer snaps to the first point to close. */
const CLOSE_SNAP = 10
/** A click this close to the last point (screen px) adds nothing; double-clicks land twice. */
const SAME_POINT = 2
const ANGLE_STEP = Math.PI / 4

interface Chain {
  points: Point[]
  stroke: string
  strokeWidth: number
  cursor: Point | null
}

let chain: Chain | null = null

function closeTarget(p: Point): Point | null {
  if (!chain || chain.points.length < 3) return null
  const first = chain.points[0]!
  return Math.hypot(p.x - first.x, p.y - first.y) * scene.camera.z < CLOSE_SNAP ? first : null
}

/** Shift locks the segment from the last point to 45 degree steps. */
function constrain(p: Point, shift: boolean): Point {
  const last = chain?.points[chain.points.length - 1]
  if (!shift || !last) return p
  const len = Math.hypot(p.x - last.x, p.y - last.y)
  const angle = Math.round(Math.atan2(p.y - last.y, p.x - last.x) / ANGLE_STEP) * ANGLE_STEP
  return { x: last.x + Math.cos(angle) * len, y: last.y + Math.sin(angle) * len }
}

function flat(points: Point[]): number[] {
  return points.flatMap((p) => [p.x, p.y])
}

function preview(): void {
  if (!chain) return
  const snap = chain.cursor ? closeTarget(chain.cursor) : null
  const tail = snap ? [] : chain.cursor ? [chain.cursor] : []
  setLiveStroke({
    points: flat([...chain.points, ...tail]),
    stroke: chain.stroke,
    strokeWidth: chain.strokeWidth,
    polyline: true,
    closed: !!snap,
    closeAt: snap,
  })
}

function onMove(e: PointerEvent): void {
  if (!chain) return
  chain.cursor = constrain(clientToWorld(e), e.shiftKey)
  preview()
}

function onKey(e: KeyboardEvent): void {
  if (e.key !== 'Escape' && e.key !== 'Enter') return
  // Ahead of the global shortcuts, which would otherwise take Escape to mean
  // "back to the select tool" and drop the chain.
  e.preventDefault()
  e.stopImmediatePropagation()
  finish(false)
}

function stop(): void {
  window.removeEventListener('pointermove', onMove)
  window.removeEventListener('keydown', onKey, true)
  setLiveStroke(null)
  chain = null
}

/** Commit what has been placed; fewer than two points is nothing to keep. */
function finish(closed: boolean): void {
  if (!chain) return
  const { points, stroke, strokeWidth } = chain
  stop()
  if (points.length < 2) return
  const node = {
    ...createPolyline(flat(points), closed),
    stroke,
    strokeWidth,
    ...(closed ? { fill: style.fill } : {}),
  }
  addNodes([node], closed ? 'Polygon' : 'Line')
  select([node.id])
  useTool('select')
}

createRoot(() => {
  // Switching tools mid-chain keeps what was drawn.
  createEffect(on(activeTool, (tool) => tool !== 'line' && finish(false), { defer: true }))
})

export const chainTool: Tool = {
  cursor: 'crosshair',

  onPointerDown(ctx) {
    if (!chain) {
      chain = { points: [ctx.world], stroke: style.stroke, strokeWidth: style.strokeWidth, cursor: null }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('keydown', onKey, true)
      preview()
      return
    }

    const p = constrain(ctx.world, ctx.event.shiftKey)
    if (closeTarget(p)) {
      finish(true)
      return
    }
    const last = chain.points[chain.points.length - 1]!
    if (Math.hypot(p.x - last.x, p.y - last.y) * scene.camera.z < SAME_POINT) return
    chain.points.push(p)
    preview()
  },

  onDoubleClick() {
    finish(false)
  },

  onSecondaryDown() {
    finish(false)
  },
}
